import React from 'react';

const CookiesPage: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Cookies Policy</h1>

        <div className="prose prose-invert prose-headings:font-medium prose-headings:mb-4 prose-p:mb-4 prose-p:text-[var(--color-muted)] prose-li:text-[var(--color-muted)] prose-li:mb-2 max-w-none">
          <p className="text-white text-lg mb-6">
            Last updated: June 15, 2023
          </p>

          <h2 className="text-2xl">Introduction</h2>
          <p>
            Developer Marketplace ("we", "us", or "our") uses cookies and similar technologies on our website. This Cookies Policy explains what cookies are, how we use them, your choices regarding cookies, and further information about cookies.
          </p>

          <h2 className="text-2xl">What Are Cookies</h2>
          <p>
            Cookies are small pieces of text sent to your web browser by a website you visit. A cookie file is stored in your web browser and allows the Service or a third-party to recognize you and make your next visit easier and the Service more useful to you.
          </p>
          <p>
            Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your personal computer or mobile device when you go offline, while session cookies are deleted as soon as you close your web browser.
          </p>

          <h2 className="text-2xl">How Developer Marketplace Uses Cookies</h2>
          <p>
            When you use and access our Service, we may place a number of cookie files in your web browser. We use cookies for the following purposes:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Essential cookies:</strong> These cookies are required for the operation of our website. They include, for example, cookies that enable you to log into secure areas of our website, use a shopping cart, or make use of e-billing services.
            </li>
            <li>
              <strong>Functionality cookies:</strong> These cookies allow us to recognize you when you return to our website. This enables us to personalize our content for you, greet you by name, and remember your preferences (for example, your choice of language or region).
            </li>
            <li>
              <strong>Analytical cookies:</strong> These cookies allow us to recognize and count the number of visitors and to see how visitors move around our website when they are using it. This helps us to improve the way our website works, for example, by ensuring that users are finding what they are looking for easily.
            </li>
            <li>
              <strong>Marketing cookies:</strong> These cookies record your visit to our website, the pages you have visited, and the links you have followed. We will use this information to make our website and the advertising displayed on it more relevant to your interests. We may also share this information with third parties for this purpose.
            </li>
          </ul>

          <h2 className="text-2xl">Third-Party Cookies</h2>
          <p>
            In addition to our own cookies, we may also use various third-party cookies to report usage statistics of the Service, deliver advertisements on and through the Service, and so on. These cookies may include:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Google Analytics</li>
            <li>Google Ads</li>
            <li>Facebook Pixel</li>
            <li>Stripe</li>
            <li>Intercom</li>
          </ul>

          <h2 className="text-2xl">What Are Your Choices Regarding Cookies</h2>
          <p>
            If you'd like to delete cookies or instruct your web browser to delete or refuse cookies, please visit the help pages of your web browser. Please note, however, that if you delete cookies or refuse to accept them, you might not be able to use all of the features we offer, you may not be able to store your preferences, and some of our pages might not display properly.
          </p>
          <p>
            For the Chrome web browser, please visit this page from Google: <a href="https://support.google.com/accounts/answer/32050" className="text-[#7F56D9] hover:underline">https://support.google.com/accounts/answer/32050</a>
          </p>
          <p>
            For the Firefox web browser, please visit this page from Mozilla: <a href="https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored" className="text-[#7F56D9] hover:underline">https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored</a>
          </p>
          <p>
            For the Safari web browser, please visit this page from Apple: <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" className="text-[#7F56D9] hover:underline">https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac</a>
          </p>
          <p>
            For any other web browser, please visit your web browser's official web pages.
          </p>

          <h2 className="text-2xl">Cookie Preferences Tool</h2>
          <p>
            You can manage your cookie preferences through our cookie consent manager tool that appears when you first visit our website. You can update your preferences at any time by clicking on the "Cookie Preferences" link in the footer of our website.
          </p>

          <h2 className="text-2xl">Do Not Track</h2>
          <p>
            Please note that we do not alter our Site's data collection and use practices when we see a Do Not Track signal from your browser.
          </p>

          <h2 className="text-2xl">Changes to This Cookies Policy</h2>
          <p>
            We may update our Cookies Policy from time to time. We will notify you of any changes by posting the new Cookies Policy on this page and updating the "Last updated" date at the top.
          </p>
          <p>
            You are advised to review this Cookies Policy periodically for any changes. Changes to this Cookies Policy are effective when they are posted on this page.
          </p>

          <h2 className="text-2xl">Contact Us</h2>
          <p>
            If you have any questions about our Cookies Policy, please contact us:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>By email: privacy@developermarketplace.com</li>
            <li>By visiting the contact page on our website</li>
          </ul>

          <div className="bg-[#1F1A27] p-6 rounded-lg mt-8">
            <h3 className="text-xl mb-4">Cookie Settings</h3>
            <p>
              You can adjust your cookie preferences here. These settings may affect your browsing experience and the functionality of our website.
            </p>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Essential Cookies</p>
                  <p className="text-sm text-[var(--color-muted)]">Always active and required for basic functionality</p>
                </div>
                <div className="relative">
                  <input type="checkbox" className="sr-only" id="essential-cookies" checked disabled />
                  <div className="block bg-gray-600 w-14 h-8 rounded-full"></div>
                  <div className="dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition translate-x-6"></div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Functionality Cookies</p>
                  <p className="text-sm text-[var(--color-muted)]">Enhance your experience with personalization</p>
                </div>
                <div className="relative">
                  <input type="checkbox" className="sr-only" id="functionality-cookies" />
                  <div className="block bg-gray-600 w-14 h-8 rounded-full"></div>
                  <div className="dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition"></div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Analytics Cookies</p>
                  <p className="text-sm text-[var(--color-muted)]">Help us improve our website by collecting anonymous data</p>
                </div>
                <div className="relative">
                  <input type="checkbox" className="sr-only" id="analytics-cookies" />
                  <div className="block bg-gray-600 w-14 h-8 rounded-full"></div>
                  <div className="dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition"></div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Marketing Cookies</p>
                  <p className="text-sm text-[var(--color-muted)]">Allow us to provide personalized advertisements</p>
                </div>
                <div className="relative">
                  <input type="checkbox" className="sr-only" id="marketing-cookies" />
                  <div className="block bg-gray-600 w-14 h-8 rounded-full"></div>
                  <div className="dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition"></div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button className="btn-primary py-2 px-6 mr-3">Save Preferences</button>
              <button className="bg-[#2A2633] text-white py-2 px-6 rounded-md hover:bg-[#2A2633]/80 transition-colors">Accept All</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiesPage;
